import React, { useState } from 'react';
import GroupNavigator from './GroupNavigator';
import PropertyGroupManager from './PropertyGroupManager';
import PropertyDetailDrawer from './PropertyDetailDrawer';
import DashboardWidgets from './DashboardWidgets';
import Navbar from '../Dashboard/components/Navbar';
import { complianceService } from '../../services/api';
import './HousingPro.css';

interface HousingDashboardProps {
    user: any;
}

const HousingDashboard: React.FC<HousingDashboardProps> = ({ user }) => {
    const [selectedGroupId, setSelectedGroupId] = useState<string>('all');
    const [isManagerOpen, setIsManagerOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
    const [portfolioSize, setPortfolioSize] = useState<number | null>(null);

    React.useEffect(() => {
        complianceService.getProperties()
            .then((res: any) => {
                let list: any[] = [];
                if (Array.isArray(res.data)) list = res.data;
                else if (Array.isArray(res.data?.data)) list = res.data.data;
                else if (Array.isArray(res.data?.properties)) list = res.data.properties;
                setPortfolioSize(list.filter((item: any) => item && (item.address || item.postcode)).length);
            })
            .catch(console.error);
    }, []);

    return (
        <div className="hp-dashboard-container">
            <Navbar 
                expiringSoon={0}
                overdue={0}
            />
            
            <header className="hp-header">
                <div>
                    <div className="flex items-center gap-3">
                        <h1>Command Centre</h1>
                        <div className="hp-plan-badge">H-PRO OPS</div>
                    </div>
                    <p className="hp-welcome">System active. {portfolioSize !== null ? `Monitoring ${portfolioSize} units for ${user.given_name || 'Admin'}.` : `Portfolio overview for ${user.given_name || 'Admin'}.`}</p>
                    
                    {portfolioSize !== null && (
                        <div className="mt-4 bg-white rounded-xl shadow-sm border border-slate-100 p-4 inline-block min-w-[180px]">
                            <div className="flex items-center gap-2 mb-2 text-xs font-bold text-slate-500 tracking-wider">
                                <i className="fas fa-building text-slate-400"></i> PORTFOLIO SIZE
                            </div>
                            <div className="text-3xl font-black text-slate-900 mb-1">{portfolioSize}</div>
                            <div className="text-xs font-semibold text-slate-600">Managed Units</div>
                        </div>
                    )}
                </div>
                
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setIsManagerOpen(true)}
                        className="bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-slate-800 transition-all flex items-center gap-2 shadow-lg"
                    >
                        <i className="fas fa-layer-group text-emerald-400"></i>
                        Portfolio Ops
                    </button>
                </div>
            </header>

            <div className="hp-main-panels">
                {/* Left Panel - Group Navigator */}
                <aside className="hp-left-panel flex flex-col gap-4">
                    <GroupNavigator
                        activeId={selectedGroupId}
                        onSelect={(id: string) => setSelectedGroupId(id)}
                        refreshKey={refreshKey}
                    />
                </aside>

                {/* Main Panel - Draggable Dashboard Widgets */}
                <main className="hp-right-panel">
                    <DashboardWidgets user={user} selectedGroupId={selectedGroupId} />
                </main>
            </div>

            {isManagerOpen && (
                <PropertyGroupManager 
                    onClose={() => setIsManagerOpen(false)} 
                    onRefresh={() => setRefreshKey(prev => prev + 1)}
                />
            )}

            {selectedPropertyId && (
                <PropertyDetailDrawer 
                    propertyId={selectedPropertyId} 
                    onClose={() => setSelectedPropertyId(null)} 
                />
            )}
        </div>
    );
};

export default HousingDashboard;
